/**
 * How the renderer decides it is serving a career site, and how it asks the
 * platform for a page. Kept free of React and of the block components so the
 * eager bundle only carries this file; the renderer itself is lazy-loaded.
 */
import { api } from '../services/api';
import type {
  ApiCareerPageResponse,
  ApiCareerSite,
  CareerPageResponse,
  CareerRoute,
  CareerSite,
  CareerTheme,
  PageLocation,
  PortalKind,
} from './types';

export const CAREER_PATH_PREFIX = '/career/';
export const CAREER_PREVIEW_PATH = '/career-preview';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * First path segments the legacy routes own on every host. A career site never
 * claims them, so today's portals keep working on a host that also serves a
 * career site.
 */
const LEGACY_ROOT_SEGMENTS = new Set([
  'internal',
  'referral',
  'sites',
  'p',
  'candidate',
  'checklist',
  'certifications',
  '404',
  'error',
]);

const NEGATIVE_CACHE_PREFIX = 'orbio_career_host_';
const NEGATIVE_CACHE_TTL_MS = 60 * 60 * 1000;

/** How long the host probe may take before today's routes take over. */
export const HOST_PROBE_TIMEOUT_MS = 3000;

/** Hostnames allowed into host mode, from a comma-separated list; empty keeps host mode off. */
export function parseCareerHosts(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Whether a custom domain may serve a career site at all. The build lists the
 * hosts in `VITE_CAREER_HOSTS`; every other custom domain never asks the
 * platform and behaves exactly as today.
 */
export function isCareerHost(
  hostname: string,
  careerHosts: string[] = parseCareerHosts(import.meta.env.VITE_CAREER_HOSTS),
): boolean {
  return careerHosts.includes(hostname.toLowerCase());
}

/** What a site shows before its settings are published. */
export const DEFAULT_THEME: CareerTheme = {
  primary_color: '#1f2937',
  secondary_color: '#f3f4f6',
  font_heading: 'system',
  font_body: 'system',
  radius: 'md',
  button_style: 'solid',
  logo: null,
  favicon: null,
  header_style: 'solid',
  logo_on_dark: null,
  heading_scale: 'regular',
  button_radius: null,
  surface_style: 'plain',
  footer_style: 'simple',
};

export function normalisePath(pathname: string): string {
  const collapsed = pathname.replace(/\/{2,}/g, '/');
  if (collapsed.length > 1 && collapsed.endsWith('/')) return collapsed.slice(0, -1);
  return collapsed || '/';
}

/**
 * Which career-site mode, if any, a URL asks for.
 *
 * - `/career-preview` is the editor preview.
 * - `/career/<slug>/…` is path mode, on any host.
 * - On a custom domain listed in `VITE_CAREER_HOSTS` every other path is a
 *   career-site candidate, unless a legacy route owns its first segment or the
 *   host is already known not to serve a career site.
 */
export function detectCareerRoute(
  pathname: string,
  hostname: string,
  customDomain: boolean,
  careerHosts?: string[],
): CareerRoute | null {
  const path = normalisePath(pathname);

  if (path === CAREER_PREVIEW_PATH) {
    return { mode: 'preview' };
  }

  if (path.startsWith(CAREER_PATH_PREFIX)) {
    const rest = path.slice(CAREER_PATH_PREFIX.length);
    const slash = rest.indexOf('/');
    const siteSlug = slash === -1 ? rest : rest.slice(0, slash);
    if (!siteSlug) return null;
    const sitePath = slash === -1 ? '/' : rest.slice(slash);
    return { mode: 'path', siteSlug, sitePath };
  }

  if (!customDomain) return null;
  if (!isCareerHost(hostname, careerHosts)) return null;

  const first = path.split('/')[1] || '';
  if (LEGACY_ROOT_SEGMENTS.has(first)) return null;
  if (isHostKnownWithoutCareerSite(hostname)) return null;

  return { mode: 'host', host: hostname, sitePath: path };
}

/** Prefix every in-site link carries in this mode. */
export function basePathFor(route: CareerRoute): string {
  return route.mode === 'path' ? `${CAREER_PATH_PREFIX}${route.siteSlug}` : '';
}

/** The path inside the site that a browser pathname names in this mode. */
export function sitePathFor(route: Exclude<CareerRoute, { mode: 'preview' }>, pathname: string): string {
  if (route.mode === 'host') return normalisePath(pathname);
  return normalisePath(pathname.slice(basePathFor(route).length) || '/');
}

function portalKind(segment: string): PortalKind | null {
  return segment === 'internal' || segment === 'referral' ? segment : null;
}

/**
 * What a site path means given the page that answered it: the page itself,
 * a position under it (`{page path}/{slug}/{uuid}`), one of its portals
 * (`{page path}/internal`, `{page path}/referral`, each with its own
 * positions one level down), or nothing.
 */
export function locateInPage(sitePath: string, pagePath: string): PageLocation {
  const path = normalisePath(sitePath);
  const root = normalisePath(pagePath);
  if (path === root) return { kind: 'page' };
  const prefix = root === '/' ? '/' : `${root}/`;
  if (!path.startsWith(prefix)) return { kind: 'not-found' };
  const rest = path.slice(prefix.length).split('/');
  if (rest.length === 2 && rest[0] && UUID_RE.test(rest[1])) {
    return { kind: 'position', slug: rest[0], uuid: rest[1].toLowerCase() };
  }
  const portal = portalKind(rest[0]);
  if (portal && rest.length === 1) return { kind: 'portal', portal, position: null };
  if (portal && rest.length === 3 && rest[1] && UUID_RE.test(rest[2])) {
    return { kind: 'portal', portal, position: { slug: rest[1], uuid: rest[2].toLowerCase() } };
  }
  return { kind: 'not-found' };
}

/** Join path pieces without doubling slashes. */
export function joinPath(...parts: string[]): string {
  const joined = parts
    .filter((part) => part && part !== '/')
    .map((part) => part.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
  return `/${joined}`;
}

/** Query parameters that identify a page to `GET /career/page`. */
export function careerPageParams(
  route: Exclude<CareerRoute, { mode: 'preview' }>,
  sitePath: string,
): Record<string, string> {
  return route.mode === 'path'
    ? { site: route.siteSlug, path: sitePath }
    : { host: route.host, path: sitePath };
}

/** Why `GET /career/page` answered 404, read from its `detail`. */
export type NotFoundReason = 'site' | 'page' | 'unknown';

export class CareerSiteNotFoundError extends Error {
  reason: NotFoundReason;

  constructor(reason: NotFoundReason = 'unknown') {
    super('career-site-not-found');
    this.name = 'CareerSiteNotFoundError';
    this.reason = reason;
  }
}

function notFoundReason(error: unknown): NotFoundReason | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 404) return null;
  const detail = typeof response.data?.detail === 'string' ? response.data.detail.toLowerCase() : '';
  if (detail.includes('career site')) return 'site';
  if (detail.includes('page')) return 'page';
  return 'unknown';
}

/** A site with its published settings, or defaults while it has none. */
export function withSiteDefaults(site: ApiCareerSite): CareerSite {
  return {
    ...site,
    theme: { ...DEFAULT_THEME, ...(site.theme ?? {}) },
    navigation: {
      header: site.navigation?.header ?? [],
      footer: {
        legal: site.navigation?.footer?.legal ?? [],
        social: site.navigation?.footer?.social ?? [],
        groups: site.navigation?.footer?.groups ?? [],
        tagline: site.navigation?.footer?.tagline ?? null,
        copyright: site.navigation?.footer?.copyright ?? null,
      },
      countries: site.navigation?.countries ?? [],
    },
    head: {
      gtm_container_id: site.head?.gtm_container_id ?? null,
      consent: site.head?.consent ?? null,
      search_console_token: site.head?.search_console_token ?? null,
    },
  };
}

export function normalisePageResponse(data: ApiCareerPageResponse): CareerPageResponse {
  return {
    match: data.match === 'ancestor' ? 'ancestor' : 'exact',
    site: withSiteDefaults(data.site),
    page: data.page,
    media: data.media ?? {},
  };
}

/**
 * The served page answering a site path, exact or nearest ancestor. Throws
 * `CareerSiteNotFoundError` on 404, with the reason the platform gives.
 */
export async function fetchCareerPage(
  route: Exclude<CareerRoute, { mode: 'preview' }>,
  sitePath: string,
  options: { timeout?: number } = {},
): Promise<CareerPageResponse> {
  try {
    const response = await api.get<ApiCareerPageResponse>('/career/page', {
      params: careerPageParams(route, sitePath),
      timeout: options.timeout,
    });
    return normalisePageResponse(response.data);
  } catch (error) {
    const reason = notFoundReason(error);
    if (reason) throw new CareerSiteNotFoundError(reason);
    throw error;
  }
}

/** The draft a preview token points at. */
export async function fetchCareerPreview(token: string): Promise<CareerPageResponse> {
  const response = await api.get<ApiCareerPageResponse>('/career/preview', { params: { token } });
  return normalisePageResponse(response.data);
}

// --- Hosts that answered 404: remembered so the legacy flow starts at once ---

export function isHostKnownWithoutCareerSite(hostname: string): boolean {
  try {
    const raw = localStorage.getItem(NEGATIVE_CACHE_PREFIX + hostname);
    if (!raw) return false;
    const expiry = Number(raw);
    if (!Number.isFinite(expiry) || Date.now() > expiry) {
      localStorage.removeItem(NEGATIVE_CACHE_PREFIX + hostname);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function rememberHostWithoutCareerSite(hostname: string): void {
  try {
    localStorage.setItem(NEGATIVE_CACHE_PREFIX + hostname, String(Date.now() + NEGATIVE_CACHE_TTL_MS));
  } catch {
    // localStorage unavailable — the next load probes again
  }
}

export function forgetHostWithoutCareerSite(hostname: string): void {
  try {
    localStorage.removeItem(NEGATIVE_CACHE_PREFIX + hostname);
  } catch {
    // nothing to forget
  }
}

/** True when a region probe answered with a career page rather than a job feed. */
export function isCareerPageResponse(data: unknown): data is ApiCareerPageResponse {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as Partial<ApiCareerPageResponse>;
  return typeof candidate.site === 'object' && candidate.site !== null && typeof candidate.page === 'object' && candidate.page !== null;
}
